import { Components, PaletteMode, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'primary' | 'secondary' | 'link' | 'outline' | 'ghost' | 'destructive';
  }

  interface IconButtonPropsSizeOverrides {
    xlarge: true;
  }

  interface IconButtonPropsSizeOverrides {
    xlarge: true;
  }
}

export const MuiIconButtonTheme = (mode: PaletteMode): Components<Theme>['MuiIconButton'] => {
  const { tokens, componentTokens } = themePalette(mode);

  return {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: () => {
        return {
          padding: 0,
          outline: 'none',
          transition:
            'border-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          border: '1px solid',
          color: tokens.textPrimary,
          lineHeight: 0,
          // Size variants
          '&.MuiIconButton-sizeSmall': {
            width: '28px',
            height: '28px',
          },
          '&.MuiIconButton-sizeMedium': {
            width: '38px',
            height: '38px',
          },
          '&.MuiIconButton-sizeLarge': {
            width: '45px',
            height: '45px',
          },
          '&.MuiIconButton-sizeXlarge': {
            width: '48px',
            height: '48px',
          },
        };
      },
    },
    variants: [
      // Primary variant - matches MuiButton primary
      {
        props: { variant: 'primary' },
        style: {
          color: componentTokens.buttons.colors.coreIcon.primary.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.primary.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.primary.assets.onHover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.primary.assets.onHover,
            },
          },
          '&:active, &[data-active="true"]': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.primary.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.resting,
            opacity: 0.4,
          },
        },
      },
      // Secondary variant - matches MuiButton secondary
      {
        props: { variant: 'secondary' },
        style: {
          color: componentTokens.buttons.colors.coreIcon.secondary.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.secondary.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.secondary.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.secondary.assets.hover,
            },
          },
          '&:active, &[data-active="true"]': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.secondary.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.resting,
            opacity: 0.4,
          },
        },
      },
      // Link variant - matches MuiButton link
      {
        props: { variant: 'link' },
        style: {
          border: 'none',
          color: componentTokens.buttons.colors.link.primary.text.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.primary.text.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.link.primary.text.selected,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.primary.text.selected,
          },
        },
      },
      // Outline variant - matches MuiButton outline
      {
        props: { variant: 'outline' },
        style: {
          color: componentTokens.buttons.colors.coreIcon.outline.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.outline.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.hover,
            },
          },
          '&:active, &[data-active="true"]': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.resting,
            opacity: 0.4,
          },
        },
      },
      // Ghost variant - matches MuiButton ghost
      {
        props: { variant: 'ghost' },
        style: {
          color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.ghost.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
            },
          },
          '&:active, &[data-active="true"]': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
            opacity: 0.4,
          },
        },
      },
      // Destructive variant - matches MuiButton destructive
      {
        props: { variant: 'destructive' },
        style: {
          color: componentTokens.buttons.colors.coreIcon.destructive.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.destructive.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.destructive.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.destructive.assets.hover,
            },
          },
          '&:active, &[data-active="true"]': {
            backgroundColor:
              componentTokens.buttons.colors.coreIcon.destructive.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.destructive.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.resting,
            opacity: 0.4,
          },
        },
      },
    ],
  };
};
